<form [formGroup]="form" class="flex flex-col gap-4 p-4">
  <div class="flex flex-col gap-4 rounded-md bg-white p-4 shadow-md">
    <h3 class="!m-0 !font-bold">Automation Settings</h3>

    <pr-template-editor-input
      cdkFocusInitial
      label="Title"
      formControlName="title"
      [context]="templateContext$ | async"
      [templateType]="htmlTemplateType"
    />

    @if (practices$ | async; as practices) {
      @if (practices.length > 1) {
        <mat-form-field
          class="flex flex-auto"
          subscriptSizing="dynamic"
          floatLabel="always"
        >
          <mat-label>Practice</mat-label>
          <mat-select formControlName="practiceRef" placeholder="All Practices">
            <mat-option [value]="undefined">All Practices</mat-option>
            @for (practice of practices$ | async; track practice.ref.id) {
              <mat-option [value]="practice.ref">
                {{ practice.name }}
              </mat-option>
            }
          </mat-select>

          @if (form.controls.practiceRef.value) {
            <button
              mat-icon-button
              matIconSuffix
              (click)="
                $event.stopImmediatePropagation();
                form.controls.practiceRef.reset(undefined)
              "
            >
              <mat-icon>clear</mat-icon>
            </button>
          }
        </mat-form-field>
      }
    }
  </div>

  <h2 class="!m-0 !mt-4 font-bold">When do you want this to run?</h2>

  <div class="flex flex-col gap-4 rounded-md bg-white p-4 shadow-md">
    @if (isMarketingContext$ | async) {
      <h3 class="!m-0 !font-bold">Timing</h3>
    } @else {
      <h3 class="!m-0 !font-bold">Automation Timing</h3>
    }

    @if (useRelativeTime$ | async) {
      <pr-automation-timing-form
        [value]="form.controls.timing.value"
        [context]="context$ | async"
        (formChange)="timingChange($event)"
      />
    } @else {
      <div fxLayout="row" fxLayoutGap="16px">
        <mat-form-field subscriptSizing="dynamic">
          <mat-label>Trigger Date</mat-label>
          <input
            matInput
            formControlName="triggerDate"
            required
            [matDatepicker]="triggerDate"
            (focus)="triggerDate.open()"
            (click)="triggerDate.open()"
          />
          <mat-datepicker-toggle matIconSuffix [for]="triggerDate" />
          <mat-datepicker #triggerDate />

          @if (form.controls.triggerDate.value) {
            <button
              mat-icon-button
              matIconSuffix
              aria-label="Clear"
              type="button"
              (click)="clearTriggerDate($event)"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>

        <pr-date-selector
          placeholder="Trigger Time"
          [required]="true"
          [interval]="timeInterval"
          formControlName="triggerTime"
          subscriptSizing="dynamic"
        />
      </div>
    }
  </div>

  @if (availableConditions$ | async; as availableConditions) {
    @if (availableConditions.length) {
      <div class="flex flex-col gap-4 rounded-md bg-white p-4 shadow-md">
        <div class="flex flex-row items-center gap-2">
          @if (isMarketingContext$ | async) {
            <h3 class="!m-0 !font-bold">Conditions</h3>
          } @else {
            <h3 class="!m-0 !font-bold">Automation Conditions</h3>
          }
          <mat-icon
            class="icon-sm opacity-50"
            [matTooltip]="automationConditionsHelp"
            >info</mat-icon
          >
        </div>
        <pr-conditional-logic-form
          [availableConditions]="availableConditions"
          [defaultCondition]="defaultCondition"
          [conditions]="form.value.requiredConditions"
          (conditionsUpdated)="requiredConditionsChange($event)"
        />
      </div>
    }
  }

  <h2 class="!m-0 !mt-4 font-bold">What do you want to happen?</h2>

  <div class="flex flex-col gap-4 rounded-md bg-white p-4 shadow-md">
    <h3 class="!m-0 !font-bold">Task Configuration</h3>
    <div class="flex gap-4">
      <mat-form-field class="flex flex-auto" subscriptSizing="dynamic">
        <mat-label>Assignee</mat-label>
        <mat-select formControlName="assignee" [compareWith]="compareFn">
          @if (teams$ | async; as teams) {
            <mat-optgroup label="Teams">
              @for (team of teams; track team.ref.id) {
                <mat-option [value]="team">
                  {{ team.name }}
                </mat-option>
              }
            </mat-optgroup>
          }

          @if (staff$ | async; as staff) {
            <mat-optgroup label="Staff">
              @for (staffer of staff; track staffer.ref.id) {
                <mat-option [value]="staffer">
                  {{ staffer.user.name }}
                </mat-option>
              }
            </mat-optgroup>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field class="flex flex-auto" subscriptSizing="dynamic">
        <mat-label>Priority</mat-label>
        <mat-select formControlName="priority" required>
          @for (priority of taskPriorities; track priority) {
            <mat-option [value]="priority">
              {{ priority }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <pr-template-editor-textarea
      formControlName="comment"
      [context]="templateContext$ | async"
      [templateType]="htmlTemplateType"
    />
  </div>

  @if (config$ | async; as config) {
    <pr-automation-treatments [config]="config" />
  }
</form>
